import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _find from 'lodash/find';
import _pick from 'lodash/pick';
import classNames from 'classnames';

import auth from '@udacity/ureact-hoth';

import {
  useFetchMeQuery,
  useFetchRecruitmentFamiliesQuery,
  useFetchReviewerInterestsQuery,
  useUpdateReviewerInterestMutation,
} from 'app/apis/reviews';
import {
  REVIEWER_STATUSES,
  INTEREST_LIMIT,
} from 'app/queue/domains/recruitment-families/recruitment-families-constants';
import { STATUSES as ASYNC_STATUSES } from 'app/common/domains/async';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import type { RecruitmentFamily, ReviewerInterest } from 'app/types/reviews';

import RecruitmentFamilyItem from './recruitment-family-item';
import styles from './recruitment-families.module.scss';

const CONFIRMATION_DURATION_MS = 3000;
const INTEREST_FIELDS = ['status', 'reapply_at', 'last_screened_at', 'last_expressed_at', 'updated_at'] as const;

type FamilyWithInterest = RecruitmentFamily & Partial<Pick<ReviewerInterest, typeof INTEREST_FIELDS[number]>>;
type SaveResult = 'success' | 'error';

export const RecruitmentFamilies = () => {
  const { t } = useTranslation();
  const toast = useAppToast();
  const uid = auth.getCurrentUserId() || '';
  const { data: reviewsMe, isLoading: isReviewsMeLoading } = useFetchMeQuery();
  const { data: families } = useFetchRecruitmentFamiliesQuery();
  const {
    data: interests,
    isLoading: isInterestsLoading,
    isFetching: isInterestsFetching,
  } = useFetchReviewerInterestsQuery(uid);
  const [updateReviewerInterest] = useUpdateReviewerInterestMutation();
  const [updatingFamilyId, setUpdatingFamilyId] = useState<number | null>(null);
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const confirmationTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(confirmationTimer.current), []);

  // Unlock only after the post-save refetch settles: Redux notifies between the mutation's
  // fulfilled action and the refetch's pending one, so clearing on the mutation alone flashes
  // pre-save interests.
  useEffect(() => {
    if (!saveResult || isInterestsFetching) return;

    setUpdatingFamilyId(null);
    setSaveResult(null);
    if (saveResult === 'success') {
      setIsConfirmationVisible(true);
      window.clearTimeout(confirmationTimer.current);
      confirmationTimer.current = window.setTimeout(() => setIsConfirmationVisible(false), CONFIRMATION_DURATION_MS);
    } else {
      toast({ variant: 'error', description: t('mentorProfile.interestsUpdateFailed') });
    }
  }, [saveResult, isInterestsFetching]);

  const recruitmentFamilies = useMemo(
    (): FamilyWithInterest[] =>
      (families ?? []).map((family) => ({
        ...family,
        ..._pick(_find(interests, { recruitment_family_id: family.id }), INTEREST_FIELDS),
      })),
    [families, interests]
  );

  if (isReviewsMeLoading || isInterestsLoading) return null;

  const totalSelected = recruitmentFamilies.filter((family) => family.status === REVIEWER_STATUSES.ACTIVE).length;
  const isUpdating = updatingFamilyId !== null;

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (isUpdating) return;

    const recruitmentFamilyId = parseInt(e.target.value);
    setUpdatingFamilyId(recruitmentFamilyId);

    const result = await updateReviewerInterest({
      uid,
      recruitment_family_id: recruitmentFamilyId,
      user_id: reviewsMe?.id,
      status: e.target.checked ? REVIEWER_STATUSES.ACTIVE : REVIEWER_STATUSES.INACTIVE,
    });

    setSaveResult('data' in result ? 'success' : 'error');
  };

  const confirmationClasses = classNames(styles.confirmation, {
    [styles['confirmation-visible']]: isConfirmationVisible,
  });

  return (
    <div className={styles['form-container']}>
      <section className={styles['section']}>
        <div className={styles['header']}>
          <h2 className={styles['section-title']}>{t('mentorProfile.topicsOfInterest')}</h2>
          <p className={styles['prompt-text']}>{t('mentorProfile.expertise')}</p>
        </div>
        <p className={styles['counter']}>
          {t('mentorProfile.interestsRemaining', {
            count: INTEREST_LIMIT - totalSelected,
          })}
        </p>
        <div className={confirmationClasses}>{t('mentorProfile.interestsUpdated')}</div>
        <ul aria-busy={isUpdating}>
          {recruitmentFamilies.map((family) => {
            const isDisabled =
              isUpdating || (totalSelected >= INTEREST_LIMIT && family.status !== REVIEWER_STATUSES.ACTIVE);

            return (
              <RecruitmentFamilyItem
                key={family.id}
                family={family}
                asyncStatus={family.id === updatingFamilyId ? ASYNC_STATUSES.PENDING : undefined}
                handleChange={handleChange}
                isDisabled={isDisabled}
              />
            );
          })}
        </ul>
      </section>
    </div>
  );
};

export default RecruitmentFamilies;
